import type { User as EntityUser } from '@/entities/User';
import { UserStatusEnum } from '@/entities/User';
import { DEFAULT_ORDER_OPTION } from '@/shared/config';
import { formatCivilDateToPtBR, type ExportColumnDef } from '@/shared/lib';

// TODO: campos mock (registration, department, status, excluded, admissionDate, dismissalDate)
export interface AdminUser {
  id: number;
  name: string;
  email?: string;
  username?: string;
  role?: string;
  registration: string;
  department: string;
  position: string;
  functionalActivity: string;
  status: 'active' | 'inactive';
  excluded: boolean;
  admissionDate: string;
  dismissalDate?: string;
  createdAt?: string | null;
  creator?: { id: number; name: string } | null;
  updatedAt?: string | null;
  updater?: { id: number; name: string } | null;
}

/**
 * Maps a User entity from the backend to an AdminUser object for the admin UI.
 *
 * Extracts user information including membership data (department, position, functional activities)
 * and normalizes the status field from backend format ('ACTIVE'/'INACTIVE') to frontend format
 * ('active'/'inactive'). Provides fallback values ('-') for missing optional fields.
 *
 * @param user - User entity from the backend API
 * @returns AdminUser object formatted for admin table display
 */
export function mapEntityUserToAdminUser(user: EntityUser): AdminUser {
  const firstMembership = user.memberships?.[0];
  const functionalActivityNames =
    firstMembership?.functionalActivities
      ?.map((activity) => activity.functionalActivity?.name)
      .filter((name): name is string => Boolean(name)) ?? [];

  /**
   * Normalizes user status from backend format to frontend display format.
   * Converts 'ACTIVE' to 'active' and treats undefined/null/other values as 'inactive'.
   * Uses case-insensitive comparison to handle potential case variations from backend.
   *
   * @param status - User status from backend (optional)
   * @returns Normalized status for UI display ('active' or 'inactive')
   */
  const normalizeStatus = (status?: string): 'active' | 'inactive' => {
    if (!status) return 'inactive';
    return status.toUpperCase() === UserStatusEnum.ACTIVE ? 'active' : 'inactive';
  };

  return {
    id: user.id ?? 0,
    name: user.name ?? '',
    email: user.email,
    username: user.username,
    role: user.role,
    position:
      (firstMembership?.positions?.length
        ? firstMembership.positions.map((p) => p.name).join(', ')
        : null) ??
      firstMembership?.position?.name ??
      firstMembership?.positionName ??
      '-',
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    registration: user.registrationNumber?.trim() || '-',
    department: firstMembership?.branch?.name ?? '-',
    status: normalizeStatus(user.status),
    functionalActivity:
      functionalActivityNames.length > 0 ? functionalActivityNames.join(', ') : '-',
    excluded: false,
    admissionDate: user.admissionDate ?? user.createdAt ?? '-',
    dismissalDate: user.dismissalDate,
    creator: user.creator ?? null,
    updater: user.updater ?? null,
  };
}

export const userOrderOptions = [
  { ...DEFAULT_ORDER_OPTION, label: 'Mais recente' },
  { label: 'Mais antigo', sort: 'createdAt', order: 'asc' },
  { label: 'Ordem alfabética crescente', sort: 'name', order: 'asc' },
  { label: 'Ordem alfabética decrescente', sort: 'name', order: 'desc' },
  { label: 'Maior número de matrícula', sort: 'registrationNumber', order: 'desc' },
  { label: 'Menor número de matrícula', sort: 'registrationNumber', order: 'asc' },
];

export const userTableHeaders = [
  'Usuários',
  'Matrícula',
  'Status',
  'Data admissão',
  'Data demissão',
  'Ações',
];

export const userExportColumns: ExportColumnDef<AdminUser>[] = [
  { header: 'Usuários', getValue: (u) => u.name, width: 30 },
  { header: 'Matrícula', getValue: (u) => u.registration, width: 15 },
  { header: 'Departamento', getValue: (u) => u.department, width: 25 },
  { header: 'Cargo', getValue: (u) => u.position, width: 25 },
  { header: 'Atividade Funcional', getValue: (u) => u.functionalActivity ?? '-', width: 25 },
  { header: 'Status', getValue: (u) => (u.status === 'active' ? 'Ativo' : 'Inativo'), width: 12 },
  { header: 'Data admissão', getValue: (u) => formatCivilDateToPtBR(u.admissionDate), width: 18 },
  {
    header: 'Data demissão',
    getValue: (u) => (u.dismissalDate ? formatCivilDateToPtBR(u.dismissalDate) : ''),
    width: 18,
  },
  { header: 'Criado por', getValue: (u) => u.creator?.name ?? '', width: 20 },
  { header: 'Atualizado por', getValue: (u) => u.updater?.name ?? '', width: 20 },
];
